export const FISHYMAP_BRIDGE_INPUT_SUBMITTED_EVENT = "fishymap:bridge-input-submitted";
export const FISHYMAP_BRIDGE_INPUT_APPLIED_EVENT = "fishymap:bridge-input-applied";
export const FISHYMAP_BRIDGE_INPUT_FAILED_EVENT = "fishymap:bridge-input-failed";
export const FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_EVENT =
  "fishymap:bridge-input-receipt-ingress-ready";
export const FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_PROPERTY =
  "__fishymapBridgeInputReceiptIngressReady";

const BRIDGE_INPUT_RECEIPT_EVENT_TYPES = new Set([
  FISHYMAP_BRIDGE_INPUT_SUBMITTED_EVENT,
  FISHYMAP_BRIDGE_INPUT_APPLIED_EVENT,
  FISHYMAP_BRIDGE_INPUT_FAILED_EVENT,
]);
const DEFAULT_MAX_RECEIPT_OWNERS = 8;
const FAILURE_MESSAGE_MAX_LENGTH = 512;

function cloneJson(value) {
  return JSON.parse(JSON.stringify(value));
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function positiveInputSequence(value) {
  return Number.isSafeInteger(value) && value > 0;
}

function failureMessage(error) {
  if (error instanceof Error && error.message.trim()) {
    return error.message.slice(0, FAILURE_MESSAGE_MAX_LENGTH);
  }
  const message = String(error ?? "").trim();
  return (message || "Bridge input submission failed").slice(
    0,
    FAILURE_MESSAGE_MAX_LENGTH,
  );
}

export function bridgeInputReceiptIngressIsReady(shell) {
  return shell?.[FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_PROPERTY] === true;
}

export function setShellBridgeInputReceiptIngressReady(
  shell,
  ready,
  customEventConstructor = globalThis.CustomEvent,
) {
  if (!shell || typeof shell !== "object") {
    return false;
  }
  const nextReady = ready === true;
  const changed = bridgeInputReceiptIngressIsReady(shell) !== nextReady;
  shell[FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_PROPERTY] = nextReady;
  if (
    changed &&
    typeof shell.dispatchEvent === "function" &&
    typeof customEventConstructor === "function"
  ) {
    shell.dispatchEvent(
      new customEventConstructor(FISHYMAP_BRIDGE_INPUT_RECEIPT_INGRESS_READY_EVENT, {
        bubbles: true,
        detail: { ready: nextReady },
      }),
    );
  }
  return true;
}

export function normalizeBridgeInputReceipt(value) {
  if (!isPlainObject(value)) {
    throw new TypeError("FishyMap bridge input receipt must be an object");
  }
  const unsupportedKeys = Object.keys(value).filter(
    (key) => key !== "owner" && key !== "revision",
  );
  if (unsupportedKeys.length) {
    throw new TypeError(
      `FishyMap bridge input receipt has unsupported field: ${unsupportedKeys[0]}`,
    );
  }
  const owner = typeof value.owner === "string" ? value.owner.trim() : "";
  if (!owner || owner.length > 64) {
    throw new TypeError("FishyMap bridge input receipt owner must be 1-64 characters");
  }
  if (!Number.isSafeInteger(value.revision) || value.revision <= 0) {
    throw new TypeError("FishyMap bridge input receipt revision must be a positive safe integer");
  }
  return Object.freeze({ owner, revision: value.revision });
}

export function dispatchShellBridgeInputReceiptEvent(
  shell,
  eventType,
  detail,
  customEventConstructor = globalThis.CustomEvent,
) {
  if (
    !shell ||
    typeof shell.dispatchEvent !== "function" ||
    !BRIDGE_INPUT_RECEIPT_EVENT_TYPES.has(eventType) ||
    !isPlainObject(detail) ||
    typeof customEventConstructor !== "function"
  ) {
    return false;
  }
  shell.dispatchEvent(
    new customEventConstructor(eventType, {
      bubbles: true,
      detail: cloneJson(detail),
    }),
  );
  return true;
}

export function createBridgeInputReceiptTracker({
  publish = () => {},
  maxOwners = DEFAULT_MAX_RECEIPT_OWNERS,
} = {}) {
  if (typeof publish !== "function") {
    throw new TypeError("FishyMap bridge input receipt publisher must be a function");
  }
  if (!Number.isSafeInteger(maxOwners) || maxOwners <= 0) {
    throw new TypeError("FishyMap bridge input receipt owner limit must be positive");
  }

  const queuedByOwner = new Map();
  const submittedByOwner = new Map();
  let latestAppliedInputSequence = 0;

  function publishFailure(receipt, stage, error) {
    publish(FISHYMAP_BRIDGE_INPUT_FAILED_EVENT, {
      ...receipt,
      stage,
      message: failureMessage(error),
    });
  }

  function ensureOwnerCapacity(owner) {
    if (queuedByOwner.has(owner) || submittedByOwner.has(owner)) {
      return;
    }
    let activeOwnerCount = submittedByOwner.size;
    for (const queuedOwner of queuedByOwner.keys()) {
      if (!submittedByOwner.has(queuedOwner)) {
        activeOwnerCount += 1;
      }
    }
    if (activeOwnerCount < maxOwners) {
      return;
    }
    throw new RangeError("FishyMap bridge input receipt owner limit exceeded");
  }

  function queue(value) {
    const receipt = normalizeBridgeInputReceipt(value);
    ensureOwnerCapacity(receipt.owner);
    queuedByOwner.set(receipt.owner, receipt);
    return receipt;
  }

  function fail(value, error, { stage = "signal-patch" } = {}) {
    const receipt = normalizeBridgeInputReceipt(value);
    if (queuedByOwner.get(receipt.owner)?.revision === receipt.revision) {
      queuedByOwner.delete(receipt.owner);
    }
    if (submittedByOwner.get(receipt.owner)?.revision === receipt.revision) {
      submittedByOwner.delete(receipt.owner);
    }
    publishFailure(receipt, stage, error);
    return true;
  }

  function failQueued(error, { stage = "bridge-submit" } = {}) {
    if (!queuedByOwner.size) {
      return false;
    }
    const queued = [...queuedByOwner.values()];
    queuedByOwner.clear();
    for (const receipt of queued) {
      publishFailure(receipt, stage, error);
    }
    return true;
  }

  function acknowledgeReceipt(receipt, appliedInputSequence) {
    publish(FISHYMAP_BRIDGE_INPUT_APPLIED_EVENT, {
      owner: receipt.owner,
      revision: receipt.revision,
      inputSequence: receipt.inputSequence,
      appliedInputSequence,
    });
  }

  function submitted(inputSequence) {
    if (!queuedByOwner.size) {
      return false;
    }
    if (!positiveInputSequence(inputSequence)) {
      failQueued(
        new TypeError("FishyMap tracked bridge input did not produce an input sequence"),
      );
      return false;
    }

    const queued = [...queuedByOwner.values()];
    queuedByOwner.clear();
    for (const receipt of queued) {
      const submittedReceipt = Object.freeze({ ...receipt, inputSequence });
      submittedByOwner.set(receipt.owner, submittedReceipt);
      publish(FISHYMAP_BRIDGE_INPUT_SUBMITTED_EVENT, submittedReceipt);
      if (inputSequence <= latestAppliedInputSequence) {
        submittedByOwner.delete(receipt.owner);
        acknowledgeReceipt(submittedReceipt, latestAppliedInputSequence);
      }
    }
    return true;
  }

  function acknowledge(appliedInputSequence) {
    if (!Number.isSafeInteger(appliedInputSequence) || appliedInputSequence < 0) {
      throw new TypeError(
        "FishyMap applied bridge input sequence must be a non-negative safe integer",
      );
    }
    if (appliedInputSequence < latestAppliedInputSequence) {
      throw new TypeError("FishyMap applied bridge input sequence must not regress");
    }
    latestAppliedInputSequence = appliedInputSequence;
    let settled = false;
    for (const [owner, receipt] of submittedByOwner) {
      if (receipt.inputSequence > appliedInputSequence) {
        continue;
      }
      submittedByOwner.delete(owner);
      acknowledgeReceipt(receipt, appliedInputSequence);
      settled = true;
    }
    return settled;
  }

  return Object.freeze({
    acknowledge,
    fail,
    failQueued,
    hasQueued: () => queuedByOwner.size > 0,
    queue,
    submitted,
  });
}

export function submitBridgeInputPatch(bridge, patch, receiptTracker) {
  if (!bridge || typeof bridge.setState !== "function") {
    throw new TypeError("FishyMap bridge input submission requires setState");
  }
  if (
    !receiptTracker ||
    typeof receiptTracker.hasQueued !== "function" ||
    typeof receiptTracker.failQueued !== "function"
  ) {
    throw new TypeError("FishyMap bridge input submission requires a receipt tracker");
  }

  const tracksSubmission = receiptTracker.hasQueued();
  try {
    const inputSequence = bridge.setState(patch);
    if (tracksSubmission && !positiveInputSequence(inputSequence)) {
      throw new TypeError(
        "FishyMap tracked bridge input did not produce an input sequence",
      );
    }
    return inputSequence;
  } catch (error) {
    receiptTracker.failQueued(error, { stage: "bridge-submit" });
    throw error;
  }
}
